import config from 'react-global-configuration';
import type { Langcodes } from '@uncinc/react-drupal-core/types';

import type { AppOptions } from '../types';

export const IS_SERVER = !(
  typeof window !== 'undefined'
  && window.document
  && window.document.createElement
);
export const IS_CLIENT = !IS_SERVER;

export let APP_ENV = process.env.REACT_APP_ENV;
export const __DEBUG__ = APP_ENV !== 'production';
export const USE_SENTRY = process.env.REACT_APP_SENTRY_ENABLED?.toLowerCase() === "true";
export const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
export const REACT_APP_VERSION = process.env.REACT_APP_VERSION;

export let MULTILINGUAL = true;
export let DEFAULT_LANGUAGE: Langcodes = 'en';
export const SITE_NAME = '<SITE NAME>';

export let API_URL = process.env.REACT_APP_API_URL || "/api";
// export let API_URL = 'http://localhost:3000/api'; // <- For local proxy.

// Searchkit
export const SEARCH_PATH = {
  'en': '/en/search',
  'nl': '/nl/zoeken',
};

export let SEARCHKIT_HOST_URL = process.env.REACT_APP_SEARCHKIT_HOST_URL || '/api-proxy/elasticsearch-proxy?_api_proxy_uri=';
export let SEARCHKIT_INDEX_ALL_CONTENT = process.env.REACT_APP_SEARCHKIT_HOST_URL || true;

export let APP_OPTIONS: AppOptions = {
  apiUrl: API_URL,
  element: '',
  parameters: {
    env: 'production',
    multilingual: true,
    defaultLanguage: DEFAULT_LANGUAGE,
    searchkitIndexAllContent: 'elasticsearch_index_content_all',
    footerImageBlockID: 1,
    newsletterID: 'newsletter',
    copyRight: 'Company BV',
    vacancyCompanyName: 'Company BV',
    vacancyCompanyCOC: '12345678',
    vacancyCompanyURL: 'example.org',
    vacancyCompanyLogo: 'imageurl.example.org',
    vacancyEmailAddress: 'example@example.org',
    vacancyEmailSubject: 'Application',
    vacancyEmailBody: 'example@example.org',
    privacyStatementUrl: '/privacy-statement',
    siteName:'NIOZ',
    blogSubscribeWebformID: 'blog_subscribe',
    profileFunctionGroupWeights: [
      { id: 0, weight: 0, value: 'Principal scientists/Hoofdonderzoekers' },
      { id: 1, weight: 1, value: 'Postdocs/Postdocs' },
      { id: 2, weight: 2, value: 'PhDs/Promovendi' },
      { id: 3, weight: 3, value: 'Supporting staff/Ondersteunende staf' },
      { id: 4, weight: 4, value: 'Guest/Gasten' },
      { id: 5, weight: 5, value: 'NMF office/NMF hoofdkantoor' },
      { id: 6, weight: 6, value: 'Development team/Ontwikkelingsteam' },
      { id: 7, weight: 7, value: 'Crew/Bemanning' },
      { id: 8, weight: 8, value: 'Supporting staff/Ondersteunende staf' },
      { id: 9, weight: 9, value: 'Direction/Directie' },
      { id: 10, weight: 10, value: 'ICT/ICT' },
      { id: 11, weight: 11, value: 'Communications/Communicatie' },
      { id: 12, weight: 12, value: 'Human Resources/Human Resources' },
      { id: 13, weight: 13, value: 'Facility Management/Facilitair management' },
      { id: 14, weight: 14, value: 'Finance/Financiën' },
      { id: 15, weight: 15, value: 'Robotics team/Robotica-team' },
    ],
  },
};

// If the data-theme attribute is not set, set it to the current theme.
if (IS_CLIENT && !document.documentElement.getAttribute('data-theme')) {
  document.documentElement.setAttribute('data-theme', 'nioz');
}

// The value is where the breakpoint starts (mobile-first).
// These values are taken from '/src/src/styles/assets/_variables.scss'.
export const SCREEN_XS = 0; // xs
export const SCREEN_SM = 576; // sm
export const SCREEN_MD = 768; // md
export const SCREEN_LG = 1200; // lg
export const SCREEN_XL = 1560; // xl

export const MAIN_CONTENT_ID = 'main-content';

// Content types.
export const CONTENT_TYPE_ARTICLE = 'article';
export const CONTENT_TYPE_BASIC_PAGE = 'basic_page';
export const CONTENT_TYPE_FAQ = 'faq';
export const CONTENT_TYPE_HOMEPAGE = 'homepage';
export const CONTENT_TYPE_LANDING_PAGE = 'landing_page';
export const CONTENT_TYPE_OVERVIEW_PAGE = 'overview_page';
export const CONTENT_TYPE_PARTNER ='partner';
export const CONTENT_TYPE_PROJECT = 'project';
export const CONTENT_TYPE_VACANCY = 'vacancy';

// Related content hits per page.
export const RELATED_PROJECTS_HITS_PER_PAGE = 3;
export const RELATED_IMIS_PUBLICATIONS_HITS_PER_PAGE = 3;
export const RELATED_ARTICLES_HITS_PER_PAGE = 3;
export const RELATED_EXPERTISES_HITS_PER_PAGE = 10;
export const RELATED_TEAM_HITS_PER_PAGE = 12;
export const MORE_DEPARTMENTS_HITS_PER_PAGE = 3;
export const MORE_THEMES_HITS_PER_PAGE = 2;

// Cards.
export const CARD_SUBTITLE_MAX_LENGTH = 75;

// Article types.
export const ARTICLE_TYPE_BLOG = {
  en: 'Blog',
  nl: 'Blog',
};

export const ARTICLE_TYPE_NEWS = {
  en: 'News',
  nl: 'Nieuws',
};

export const IMIS_URL: string = 'https://imis.nioz.nl/imis.php?module=ref&refid=';

export const SCIENTIFIC_DEPARTMENT_BLOCK_CLASSNAME = 'extra-submenu';

// Use config to share static configuration between components and packages.
config.set({
  'SITE_NAME': SITE_NAME,
  'MULTILINGUAL': true,
  'DEFAULT_LANGUAGE': DEFAULT_LANGUAGE,
  'API_URL': API_URL,
  'REACT_APP_VERSION': REACT_APP_VERSION,
  'PAGE_TRANSITION_SPEED': 100,
  // Set freeze to false to allow overwriting the configs based on the
  // options passed to window.reactFrontend
}, { freeze: false });

export function saveConstants(options: AppOptions) {
  APP_OPTIONS = options;

  if (options.parameters.env) {
    APP_ENV = options.parameters.env;
  }

  if (options.apiUrl) {
    API_URL = options.apiUrl;
  }

  if (APP_ENV === 'development') {
    // Use proxy in dev to prevent CORS issues.
    API_URL = 'http://localhost:3000/api';
    SEARCHKIT_HOST_URL = 'http://localhost:3000/search-proxy';
  }

  if (options.parameters.multilingual) {
    MULTILINGUAL = options.parameters.multilingual;
  }

  if (options.parameters.defaultLanguage) {
    DEFAULT_LANGUAGE = options.parameters.defaultLanguage;
  }

  // Update the config.
  config.set({
    'SITE_NAME': SITE_NAME,
    'MULTILINGUAL': MULTILINGUAL,
    'DEFAULT_LANGUAGE': DEFAULT_LANGUAGE,
    'API_URL': API_URL,
    'REACT_APP_VERSION': REACT_APP_VERSION,
    'PAGE_TRANSITION_SPEED': 100,
  });
}
